import * as React from "react";
import styled from "styled-components/macro";
import { FaAngleDown } from "react-icons/fa";
import * as colors from "../styles/colors";
import * as fonts from "../styles/fonts";
import * as mq from "../styles/mediaQueries";

import {
  ListboxInput,
  ListboxButton,
  ListboxOption,
  ListboxList,
  ListboxPopover,
} from "@reach/listbox";

export function isNumber(val) {
  return !isNaN(Number(val));
}

export function amountToNumber(amount) {
  if (!amount) return "";
  return amount.replace(/,/gi, "");
}

function numberToAmount(number) {
  const intForm = parseInt(number);
  if (isNaN(intForm)) return "";
  return parseInt(number).toLocaleString("en-In");
}

export const NumberField = React.forwardRef(function NumberField(
  {
    defaultValue = "",
    onChange = () => {},
    amount = false,
    maxLength,
    ...props
  },
  ref,
) {
  const [value, setValue] = React.useState(
    amount ? numberToAmount(defaultValue) : defaultValue,
  );

  const handleChange = evt => {
    let { value: enteredValue } = evt.target;

    enteredValue = enteredValue.replace(/,/gi, "");

    if (enteredValue === "" || isNumber(enteredValue)) {
      if (enteredValue === "") setValue("");
      else {
        if (amount) {
          const amountValue = parseInt(enteredValue.trim()).toLocaleString(
            "en-In",
          );
          if (amountValue.length <= parseInt(maxLength)) setValue(amountValue);
        } else setValue(enteredValue);
      }
      onChange(evt);
    }
  };

  return (
    <TextField
      type="tel"
      value={value}
      onChange={handleChange}
      ref={ref}
      maxLength={maxLength}
      {...props}
    />
  );
});

const stylesForSize = {
  lg: {
    fontSize: "1.39em",
  },
  md: {
    fontSize: "1em",
  },
};

export const TextField = React.forwardRef(function TextField(
  { iconSrc, isError, size = "lg", decoration = "", ...props },
  ref,
) {
  return (
    <input
      type="text"
      css={`
        font-size: ${stylesForSize[size].fontSize};
        text-transform: ${decoration === "fullName" ? "capitalize" : "none"};
        height: 3.03em;
        margin-top: 2em;
        border: 1px solid;
        border-color: ${isError ? "red" : "#e0e0e0"};
        padding: 0.5em 0.96em 0.5em 1.87em;
        width: 19em;
        border-radius: 6px;
        &:focus {
          outline-color: ${isError ? "red" : colors.primary};
        }
        ${iconSrc
          ? `
          padding-left: 3em;
        background: url(${iconSrc})
          no-repeat;
        background-size: 2em;
        background-position: 0.3em 50%;

        ${mq.sm} {
          background-size: 1.2em;
        }
        `
          : ""};

        ${mq.sm} {
          font-size: 1em;
        }
      `}
      ref={ref}
      {...props}
    />
  );
});

export const RadioButton = React.forwardRef(function RadioButton(
  {
    defaultChecked = false,
    children,
    id,
    iconSrc,
    css,
    style,
    className = "",
    ...props
  },
  ref,
) {
  return (
    <div>
      <input
        type="radio"
        id={id}
        css={`
          height: 0;
          width: 0;
          opacity: 0;
          &:checked {
            & + label {
              box-shadow: 0 8px 16px 0 rgb(0 0 0 / 20%);
              border: 2px solid ${colors.primary};
              color: ${colors.primary};
            }
          }
        `}
        defaultChecked={defaultChecked}
        ref={ref}
        {...props}
      />
      <label
        htmlFor={id}
        css={`
          border: 1px solid #ddd;
          width: 10em;
          height: 3em;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: box-shadow 0.3s;
          border-radius: 0.12em;
          font-size: 1.6em;
          font-family: ${fonts.three};

          &:hover {
            box-shadow: 0 8px 16px 0 rgb(0 0 0 / 20%);
          }

          ${mq.sm} {
            font-size: 1em;
            width: 12em;
            height: 3.7em;
          }

          ${css};
        `}
        className={className}
      >
        {iconSrc ? (
          <img
            src={iconSrc}
            alt="option"
            css={`
              width: 1em;
              margin-right: 0.3em;
            `}
          />
        ) : null}
        {children}
      </label>
    </div>
  );
});

export const Dropdown = React.forwardRef(function Dropdown(
  { options, defaultValue, onChange = () => {}, ...props },
  ref,
) {
  const [value, setValue] = React.useState(defaultValue || options[0]);
  const handleChange = val => {
    setValue(val);
    onChange(val);
  };

  return (
    <>
      <select
        {...props}
        ref={ref}
        css={`
          display: none;
        `}
      >
        {options.map(option => (
          <option key={option}>{option}</option>
        ))}
      </select>
      <ListboxInput
        css={`
          width: 19em;
          background-color: #fff;
          border: 1px solid #000;
        `}
        value={value}
        onChange={handleChange}
      >
        <ListboxButton
          css={`
            width: 100%;
            display: flex;
            justify-content: space-between;
            padding: 1em;
          `}
          arrow={<FaAngleDown />}
        />
        <ListboxPopover
          css={`
            max-height: 10em;
            overflow: auto;
            border: 1px solid black;
            outline: none;
            background-color: #fff;
            width: auto;
          `}
        >
          <ListboxList
            css={`
              list-style: none;
              padding: 0;
              outline: none;
              margin: 0;
            `}
          >
            <StyledOption
              value="select"
              disabled
              css={`
                display: none;
              `}
            >
              Select
            </StyledOption>
            {options.map(option => (
              <StyledOption key={option} value={String(option)}>
                {option} Years
              </StyledOption>
            ))}
          </ListboxList>
        </ListboxPopover>
      </ListboxInput>
    </>
  );
});

//old html select

// return (
//   <div
//     css={`
//       position: relative;
//     `}
//   >
//     <FaAngleDown
//       css={`
//         position: absolute;
//         top: 50%;
//         right: 0.3em;
//         transform: translateY(-50%);
//         font-size: 1.2em;
//       `}
//     />
//     <select
//       defaultValue={"Select"}
//       {...props}
//       ref={ref}
//       css={`
//         padding: 1em;

//         -webkit-appearance: none;
//         -moz-appearance: none;
//         text-indent: 1px;
//         text-overflow: "";
//         &::-ms-expand {
//           display: none;
//         }

//         padding-right: 2em;

//         width: 19em;

//         background-color: #fff;
//       `}
//     >
//       <option disabled>Select</option>
//       {options.map(option => (
//         <option key={option}>{option}</option>
//       ))}
//     </select>
//   </div>
// );

//old label
// <div>
//   <input
//     type="radio"
//     name={question.name}
//     id={question.options[option]}
//     value={question.options[option]}
//     defaultChecked={
//       answers[question.name] === question.options[option]
//     }
//     css={`
//       height: 0;
//       width: 0;
//       opacity: 0;
//       &:checked,
//       &:focus {
//         & + label {
//           box-shadow: 0 8px 16px 0 rgb(0 0 0 / 20%);
//           border: 2px solid ${colors.primary};
//           color: ${colors.primary};
//         }
//       }
//     `}
//   />
//   <label
//     htmlFor={question.options[option]}
//     css={`
//       border: 1px solid #ddd;
//       width: 10em;
//       height: 3em;
//       display: flex;
//       align-items: center;
//       justify-content: center;
//       cursor: pointer;
//       transition: box-shadow 0.3s;
//       border-radius: 0.12em;
//       font-size: 1.6em;
//       font-family: ${fontFamilies.three};

//       &:hover {
//         box-shadow: 0 8px 16px 0 rgb(0 0 0 / 20%);
//       }
//     `}
//   >
//     {option}
//   </label>
// </div>

const StyledOption = styled(ListboxOption)`
  padding: 0.6em;
  cursor: pointer;
  &[data-current-nav] {
    background-color: #efefef;
  }
  &[data-current-selected] {
    background-color: ${colors.primary};
    color: #fff;
  }
`;
